import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Airplane, Warning } from "@phosphor-icons/react";

/** Admin dialog to edit a user's yearly paid-leave allowance. Shows live balance. */
export default function LeaveBalanceDialog({ open, onOpenChange, user, onSaved }) {
  const [allowance, setAllowance] = useState(25);
  const [adjustment, setAdjustment] = useState(0);
  const [balance, setBalance] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setAllowance(user.leave_balance_paid ?? 25);
    setAdjustment(user.leave_used_adjustment ?? 0);
    api.get(`/leave-balance/${user.id}`).then((r) => setBalance(r.data)).catch(() => setBalance(null));
  }, [open, user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, {
        leave_balance_paid: parseInt(allowance),
        leave_used_adjustment: parseInt(adjustment) || 0,
      });
      onOpenChange(false);
      setTimeout(() => toast.success("Solde de congés mis à jour"), 50);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#111] border-white/10 text-white rounded-2xl" data-testid="leave-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Airplane size={20} className="text-emerald-400" weight="duotone" />
            Solde congés — {user?.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50">
            Jours de congés payés attribués pour l&apos;année
          </DialogDescription>
        </DialogHeader>

        {balance && (
          <div className="rounded-xl bg-black/30 border border-white/5 p-3 space-y-1.5">
            <BalanceLine label="Utilisés (demandes)" value={`${balance.used_from_requests ?? balance.used}j`} color="#F59E0B" />
            {(balance.used_adjustment ?? 0) !== 0 && (
              <BalanceLine label="Ajustement manuel" value={`${balance.used_adjustment > 0 ? "+" : ""}${balance.used_adjustment}j`} color="#7CB2FF" />
            )}
            <BalanceLine label="En attente" value={`${balance.pending}j`} color="#7CB2FF" />
            <div className="h-px bg-white/10 my-1" />
            <BalanceLine label="Restants" value={`${balance.remaining}j / ${balance.allowance}j`} color={balance.over_budget ? "#EF4444" : "#10B981"} bold />
            {balance.over_budget && (
              <div className="flex items-center gap-1.5 text-[10px] text-red-400 mt-1">
                <Warning size={11} weight="fill" /> Dépassement du solde
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Allocation annuelle</Label>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={99} value={allowance} onChange={(e) => setAllowance(e.target.value)} className="bg-white/5 border-white/10" data-testid="leave-balance-input" />
            <span className="text-sm text-white/60">jours / an</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Ajustement manuel (jours pris)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={-99}
              max={99}
              value={adjustment}
              onChange={(e) => setAdjustment(e.target.value)}
              className="bg-white/5 border-white/10"
              data-testid="leave-adjustment-input"
              placeholder="0"
            />
            <span className="text-sm text-white/60">jours</span>
          </div>
          <div className="text-[10px] text-white/40 leading-relaxed">
            +N pour forcer N jours utilisés en plus (sans créer de demande) · -N pour recréditer.
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="save-balance-btn">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BalanceLine({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-white/60">{label}</span>
      <span className={`font-mono tabular-nums ${bold ? "font-bold text-sm" : ""}`} style={{ color }}>{value}</span>
    </div>
  );
}
